/*
 * Shared page + table chrome for the AD-MFA / Database-MFA screens.
 *
 * These pieces were copy-pasted across AdPoliciesPage, DbPoliciesPage,
 * ProvidersDetails, authEventLogs and logs. This module is the single home for
 * them; the implementations are lifted verbatim from DbPoliciesPage.jsx and
 * ProvidersDetails.jsx so pages built on it are pixel-identical to AD-MFA.
 */
import { useEffect, useRef } from "react";
import { Dropdown } from "antd";
import { MoreVertical, X } from "lucide-react";

import { AN_COLORS, AN_FONT, AN_MONO } from "./tokens";
import "./tableUi.css";

import { anAlpha } from "./tokens";
/** className to put on an antd <Table> so it picks up the AD table skin. */
export const AN_TABLE_CLASS = "an-table";

/* --------------------------------------------------------------- scrolling */

const SCROLLBAR_HIDE_DELAY_MS = 700;

/**
 * Reveals a horizontal scrollbar while its element is being scrolled and hides
 * it again once scrolling settles. Pairs with `.an-scroll-x` / `.an-grid-x` in
 * tableUi.css, which start the thumb transparent.
 *
 * @param capture listen in the capture phase, so scrolls originating in a
 *   descendant (e.g. MUI DataGrid's internal scrollbar) still register — scroll
 *   events don't bubble, but they do capture.
 */
export const useScrollbarWhileScrolling = ({ capture = false } = {}) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let timer;
    const onScroll = () => {
      el.classList.add("is-scrolling");
      clearTimeout(timer);
      timer = setTimeout(() => el.classList.remove("is-scrolling"), SCROLLBAR_HIDE_DELAY_MS);
    };
    el.addEventListener("scroll", onScroll, { passive: true, capture });
    return () => {
      el.removeEventListener("scroll", onScroll, { capture });
      clearTimeout(timer);
    };
  }, [capture]);
  return ref;
};

/* ------------------------------------------------------------------ layout */

export const PageShell = ({ children, style }) => (
  <div
    style={{
      minHeight: "calc(100vh - 64px)",
      background: AN_COLORS.bgWarm,
      color: AN_COLORS.ink,
      fontFamily: AN_FONT,
      padding: "22px 28px",
      ...style,
    }}
  >
    {children}
  </div>
);

/**
 * Embedded variant for screens rendered inside another page's card (the
 * Database dashboard tabs) — no background, no padding, no min-height.
 */
export const EmbeddedShell = ({ children, style }) => (
  <div style={{ color: AN_COLORS.ink, fontFamily: AN_FONT, ...style }}>{children}</div>
);

export const PageHeader = ({ eyebrow, title, subtitle, chips, actions }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "space-between",
      marginBottom: 18,
      gap: 16,
    }}
  >
    <div>
      {eyebrow && (
        <div
          style={{
            fontFamily: AN_MONO,
            fontSize: 10.5,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: AN_COLORS.muted,
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </div>
      )}
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600, letterSpacing: -0.4 }}>{title}</h1>
      {subtitle && (
        <div style={{ fontSize: 13, color: AN_COLORS.muted, marginTop: 4 }}>{subtitle}</div>
      )}
      {chips && (
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}
        >
          {chips}
        </div>
      )}
    </div>
    {actions && <div style={{ display: "flex", gap: 8 }}>{actions}</div>}
  </div>
);

export const CountChip = ({ count, label }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "baseline",
      gap: 6,
      padding: "3px 10px",
      background: AN_COLORS.surface,
      border: `1px solid ${AN_COLORS.line}`,
      borderRadius: 99,
      fontSize: 12.5,
      color: AN_COLORS.muted,
      whiteSpace: "nowrap",
    }}
  >
    <span
      style={{
        fontFamily: AN_MONO,
        fontWeight: 600,
        color: AN_COLORS.ink,
        fontVariantNumeric: "tabular-nums",
        fontSize: 13,
      }}
    >
      {Number(count || 0).toLocaleString()}
    </span>
    <span style={{ fontWeight: 500 }}>{label}</span>
  </span>
);

/**
 * Marks a screen whose data is illustrative, not real.
 *
 * These surfaces ship to production ahead of their backends so the shape of the
 * feature can be reviewed, which means the honest thing is to say so on the
 * screen itself rather than rely on anyone remembering. Sits in PageHeader's
 * `actions` slot, i.e. the top-right corner.
 */
export const MockupBadge = ({ note = "Sample data — not connected to a backend" }) => (
  <span
    title={note}
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 7,
      padding: "5px 12px",
      borderRadius: 99,
      background: "var(--an-warnBg)",
      border: "1px dashed #D99014",
      color: "var(--an-warnFg)",
      fontFamily: AN_MONO,
      fontSize: 10.5,
      fontWeight: 700,
      letterSpacing: 1,
      textTransform: "uppercase",
      whiteSpace: "nowrap",
    }}
  >
    <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#D99014" }} />
    Mockup
  </span>
);

export const TableCard = ({ children, style }) => (
  <div
    style={{
      background: AN_COLORS.surface,
      border: `1px solid ${AN_COLORS.line}`,
      borderRadius: 10,
      overflow: "hidden",
      ...style,
    }}
  >
    {children}
  </div>
);

/* ----------------------------------------------------------------- filters */

export const FilterField = ({ label, children }) => (
  <div>
    <div
      style={{
        fontSize: 11.5,
        fontWeight: 500,
        color: AN_COLORS.ink2,
        marginBottom: 6,
        fontFamily: AN_FONT,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

export const AppliedChip = ({ children, onClear }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 4px 4px 10px",
      borderRadius: 99,
      background: AN_COLORS.primaryL,
      border: `1px solid ${anAlpha(AN_COLORS.primary, 13)}`,
      color: AN_COLORS.primary,
      fontSize: 12,
      fontWeight: 500,
    }}
  >
    {children}
    <button
      type="button"
      onClick={onClear}
      aria-label="Clear filter"
      style={{
        border: "none",
        background: "transparent",
        cursor: "pointer",
        color: AN_COLORS.primary,
        padding: 0,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 18,
        height: 18,
        borderRadius: 99,
      }}
    >
      <X size={12} strokeWidth={2.4} />
    </button>
  </span>
);

/**
 * White filter card: a CSS-grid row of <FilterField>s, then the
 * "Applied: … / Clear filters / Showing N of M" summary row.
 *
 * @param columns  gridTemplateColumns for the field row
 * @param applied  [{ key, label, clear }]
 * @param showing  rows currently rendered (omit to hide the count)
 * @param total    rows available
 */
export const FilterCard = ({
  columns = "1.6fr 1fr 1fr",
  children,
  applied = [],
  onClearAll,
  showing,
  total,
}) => (
  <div
    style={{
      background: AN_COLORS.surface,
      border: `1px solid ${AN_COLORS.line}`,
      borderRadius: 10,
      padding: 14,
      marginBottom: 14,
    }}
  >
    <div style={{ display: "grid", gridTemplateColumns: columns, gap: 12, alignItems: "end" }}>
      {children}
    </div>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: 12,
        flexWrap: "wrap",
      }}
    >
      <span style={{ fontSize: 12.5, color: AN_COLORS.ink2, fontWeight: 500 }}>Applied:</span>
      {applied.length === 0 ? (
        <span style={{ fontSize: 12.5, color: AN_COLORS.muted }}>None</span>
      ) : (
        applied.map((f) => (
          <AppliedChip key={f.key} onClear={f.clear}>
            {f.label}
          </AppliedChip>
        ))
      )}
      {applied.length > 0 && onClearAll && (
        <button
          type="button"
          onClick={onClearAll}
          style={{
            border: "none",
            background: "transparent",
            color: AN_COLORS.primary,
            fontSize: 12.5,
            fontWeight: 500,
            cursor: "pointer",
            padding: 0,
          }}
        >
          Clear filters
        </button>
      )}
      {typeof showing === "number" && (
        <span
          style={{
            marginLeft: "auto",
            fontSize: 12,
            color: AN_COLORS.muted,
            fontFamily: AN_MONO,
          }}
        >
          Showing {showing}
          {typeof total === "number" ? ` of ${total}` : ""}
        </span>
      )}
    </div>
  </div>
);

/* -------------------------------------------------------------- cell atoms */

const TONES = {
  success: { bg: "var(--an-successBg)", fg: AN_COLORS.success, bd: "var(--an-successBd)" },
  warn: { bg: "var(--an-warnBg)", fg: AN_COLORS.warn, bd: "var(--an-warnBd)" },
  danger: { bg: "var(--an-dangerBg)", fg: AN_COLORS.danger, bd: "var(--an-dangerBd)" },
  info: { bg: "var(--an-infoBg)", fg: "var(--an-infoFg)", bd: "var(--an-infoBd)" },
  neutral: { bg: AN_COLORS.bg, fg: AN_COLORS.muted, bd: AN_COLORS.line },
};

/** status string (any casing) -> tone key */
export const STATUS_TONE = {
  active: "success",
  running: "success",
  connected: "success",
  enabled: "success",
  approved: "success",
  online: "success",
  healthy: "success",
  success: "success",
  inactive: "warn",
  pending: "warn",
  paused: "warn",
  suspended: "warn",
  warning: "warn",
  degraded: "warn",
  stopped: "danger",
  failed: "danger",
  error: "danger",
  disabled: "danger",
  revoked: "danger",
  offline: "danger",
  disconnected: "danger",
  "never connected": "neutral",
  unknown: "neutral",
};

const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

/**
 * Pill with a leading dot. Tone is derived from `status` unless `tone` is given.
 */
export const StatusPill = ({ status, label, tone }) => {
  const key = String(status ?? "").trim().toLowerCase();
  const t = TONES[tone || STATUS_TONE[key] || "neutral"];
  const text = label ?? (key ? titleCase(String(status).trim()) : "—");
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "2px 9px",
        borderRadius: 99,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        fontSize: 11.5,
        fontWeight: 500,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: t.fg }} />
      {text}
    </span>
  );
};

/** Mono square badge for types, roles, privileges. `truncate` caps it at its
 * cell's width with an ellipsis (pass `title` so the full text is a hover away). */
export const SquareBadge = ({ children, tone, truncate, title }) => {
  const t = tone && TONES[tone];
  return (
    <span
      title={title}
      style={{
        ...(truncate ? { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", verticalAlign: "middle" } : null),
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 5,
        background: t ? t.bg : AN_COLORS.tint,
        color: t ? t.fg : AN_COLORS.ink2,
        border: `1px solid ${t ? t.bd : AN_COLORS.line}`,
        fontFamily: AN_MONO,
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children || "—"}
    </span>
  );
};

/** Tabular / identifier cell: mono, dimmer, truncating. */
export const MonoCell = ({ children, title }) => (
  <span
    title={title ?? (typeof children === "string" ? children : undefined)}
    style={{
      fontFamily: AN_MONO,
      fontSize: 12,
      color: AN_COLORS.ink3,
      display: "block",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {children || "—"}
  </span>
);

export const iconButtonStyle = () => ({
  width: 28,
  height: 28,
  borderRadius: 5,
  border: "none",
  background: "transparent",
  color: AN_COLORS.muted,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

/** Borderless 28x28 kebab button + antd dropdown. `items` is menu.items. */
export const RowActionMenu = ({ items, ariaLabel = "Row actions", disabled }) => (
  <Dropdown trigger={["click"]} placement="bottomRight" menu={{ items }} disabled={disabled}>
    <button
      type="button"
      aria-label={ariaLabel}
      style={iconButtonStyle()}
      onClick={(e) => e.stopPropagation()}
    >
      <MoreVertical size={16} />
    </button>
  </Dropdown>
);

/* ---------------------------------------------------------------- helpers */

export const fmtDate = (dt) => {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-US", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (_) {
    return "—";
  }
};

/** antd Table `locale` — "Loading…" while fetching, otherwise the page's copy. */
export const tableEmpty = (loading, message = "No records found.") => ({
  emptyText: loading ? "Loading…" : message,
});

/** antd Table props shared by every AD-styled table. */
export const anTableProps = {
  className: AN_TABLE_CLASS,
  size: "middle",
};
